/**
 * @file useCreatePartialRefund.js
 */

import { useMutation } from "@apollo/client";
import { CREATE_PARTIAL_REFUND } from "cache/Invoice/mutations";

/**
 * @hook useCreatePartialRefund
 *
 * @description
 * Custom hook that handles the createPartialRefund mutation functionality.
 * Use this hook when you need to create a partial refund for an existing invoice.
 *
 * @returns
 * - createPartialRefund: Function to execute the createPartialRefund mutation
 * - loading: Boolean indicating the loading state
 * - error: Error object if there's an error
 */
const useCreatePartialRefund = () => {
  // Declare the createPartialRefund mutation
  const [createPartialRefundMutation, { loading, error }] = useMutation(
    CREATE_PARTIAL_REFUND
  );

  /**
   * Executes the createPartialRefund mutation.
   *
   * @param {String} invoiceId - The ID of the invoice to partially refund.
   * @param {Object} input - The partial refund input data.
   * @param {Number} input.amount - The amount to refund.
   * @param {String} input.reason - The reason for the partial refund.
   * @returns {Object} - An object containing the mutation result data or an error object.
   * @throws {Error} - Throws an error if the mutation execution fails.
   */
  const createPartialRefund = async (invoiceId, input) => {
    try {
      const { data } = await createPartialRefundMutation({
        variables: {
          invoiceId,
          input,
        },
      });
      if (error) {
        throw new Error(error);
      }
      // Process the returned data as required
      return { data: data.createPartialRefund };
    } catch (error) {
      return { error };
    }
  };

  return { createPartialRefund, loading, error };
};

export default useCreatePartialRefund;
